import { useEffect, useState } from 'react';
import moment from 'moment';
import { getSupplierInvoiceCollectionNotesReport } from '../../../../../redux/services/reportsService';
import { getSupplierCollectionSequences } from '../../../../../redux/services/collectionSequenceService';
import { getSupplierCollectionTriggers } from '../../../../../redux/services/collectionTriggerService';
import { useCollectionBuyerEngagements } from '../../CollectionTriggersAndSequences/useCollectionBuyerEngagements';
import { useCollectionSequenceBuyerProgress } from '../../CollectionTriggersAndSequences/useCollectionSequenceBuyerProgress';
import { resolveBuyerCollectionEngagement } from '../../CollectionTriggersAndSequences/resolveBuyerCollectionEngagement';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { InvoiceCollectionNotesRow } from './types';

const getDaysPastDue = (dueDate: string | null): number | null => {
  if (!dueDate) return null;
  const daysPastDue = moment().startOf('day').diff(moment(dueDate).startOf('day'), 'days');
  return daysPastDue > 0 ? daysPastDue : null;
};

export const useInvoiceCollectionNotesData = (params?: {
  startDate?: string;
  endDate?: string;
}) => {
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<InvoiceCollectionNotesRow[]>([]);

  const { user, firebaseUserId } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { invoiceCollectionNotesReport, loadingList } = useAppSelector((state) => state.reports);
  const { sequences: collectionSequences } = useAppSelector((state) => state.collectionSequences);
  const { triggers: collectionTriggers } = useAppSelector((state) => state.collectionTriggers);
  const { engagementsByBuyerId } = useCollectionBuyerEngagements(currentSupplier?.id || '');
  const { progressByBuyerId } = useCollectionSequenceBuyerProgress(currentSupplier?.id || '');
  const mainRoles = user?.roles.filter((role) => role !== 'DILL_ADMIN');

  const isLoading = loadingList.includes('getSupplierInvoiceCollectionNotesReport');

  useEffect(() => {
    if (mainRoles?.includes('SUPPLIER') && firebaseUserId && currentSupplier?.id) {
      dispatch(
        getSupplierInvoiceCollectionNotesReport({
          supplierId: currentSupplier.id,
          startDate: params?.startDate,
          endDate: params?.endDate,
        })
      );
    }
  }, [user?.id, firebaseUserId, currentSupplier?.id, params?.startDate, params?.endDate]);

  // Sequences/triggers aren't fetched by every page (Customers.tsx only fetches them on-demand
  // when the bulk-enroll modal opens), so this report needs its own fetch to resolve engagement
  // ids -> names/steps for every row.
  useEffect(() => {
    if (!currentSupplier?.id) return;
    dispatch(getSupplierCollectionSequences({ supplierId: currentSupplier.id }));
    dispatch(getSupplierCollectionTriggers({ supplierId: currentSupplier.id }));
  }, [currentSupplier?.id]);

  useEffect(() => {
    if (!invoiceCollectionNotesReport || invoiceCollectionNotesReport.length === 0) {
      setRows([]);
      return;
    }

    // A sequence step's title can reference {{amount}} (the buyer's total past due balance, not
    // any one invoice's amount) - since this report is one-row-per-invoice, sum each buyer's
    // invoices first so every row for the same buyer shows the same, consistent step text.
    const totalAmountByBuyerId = new Map<string, number>();
    invoiceCollectionNotesReport.forEach((row) => {
      totalAmountByBuyerId.set(
        row.buyerId,
        (totalAmountByBuyerId.get(row.buyerId) ?? 0) + (row.invoiceAmount ?? 0)
      );
    });

    const flattenedRows: InvoiceCollectionNotesRow[] = invoiceCollectionNotesReport.map((row) => {
      const { collectionStatus, collectionName, collectionStep } = resolveBuyerCollectionEngagement(
        {
          buyerId: row.buyerId,
          buyerName: row.businessName || '',
          amount: totalAmountByBuyerId.get(row.buyerId) ?? 0,
          engagementsByBuyerId,
          progressByBuyerId,
          collectionSequences,
          collectionTriggers,
        }
      );

      return {
        ...row,
        id: row.invoiceId || `${row.buyerId}-${row.invoiceNumber}`,
        daysPastDue: getDaysPastDue(row.dueDate),
        collectionStatus,
        collectionName,
        collectionStep,
      };
    });

    setRows(flattenedRows);
  }, [
    invoiceCollectionNotesReport,
    collectionSequences,
    collectionTriggers,
    engagementsByBuyerId,
    progressByBuyerId,
  ]);

  return { rows, isLoading };
};
